/**
 * Bootstrap PWA (W2/W4, Dual-Core IA §7): язык → восстановление сессии
 * (refresh-cookie) → PD-гейт → shell с role-based табами.
 *
 * Участник: НИКА / СЕГОДНЯ / ПРОГРЕСС / КАБИНЕТ.
 * Тренер:   ПУЛЬС / АТЛЕТЫ / ПРОГРАММЫ / РАСПИСАНИЕ / НИКА / КАБИНЕТ.
 * Access-токен живёт в памяти — перезагрузка страницы восстанавливает сессию
 * через `POST /auth/refresh` (K8, §6.1). PD-гейт «настоящий»: без согласия
 * чат не открывается (сервер вернёт 403 `pd_consent_required`).
 */

import {
  getFeatures,
  getMeContext,
  hasSession,
  logout,
  restoreSession,
} from "./api";
import { renderChat, type ChatView } from "./chat";
import { initI18n, setLang, t } from "./i18n";
import { icon } from "./icons";
import { renderLogin } from "./login";
import { registerServiceWorker } from "./push";
import { fetchPdGate, renderPdGate } from "./pdgate";
import {
  renderAthletesScreen,
  renderBriefScreen,
  renderCabinetScreen,
  renderProgressScreen,
  renderTodayScreen,
  shell,
} from "./screens";
import {
  renderBusinessScreen,
  renderProgramsScreen,
  renderPulseScreen,
  renderScheduleScreen,
} from "./coach";
import "./styles.css";

const root = document.getElementById("app")!;
let disposeShell: (() => void) | null = null;

function closeShell(): void {
  disposeShell?.();
  disposeShell = null;
}

// W3 (§11): sw.js — push-обработчик; регистрация ранняя, чтобы подписка
// работала без захода в Кабинет (best-effort, фолбэк TG — I-7).
registerServiceWorker();

type TabId =
  | "nika"
  | "today"
  | "progress"
  | "cabinet"
  | "athletes"
  | "pulse"
  | "programs"
  | "schedule"
  | "business"
  | "more";

const PARTICIPANT_TABS: TabId[] = ["nika", "today", "progress", "cabinet"];
// §7 COACH: ПУЛЬС / АТЛЕТЫ / ПРОГРАММЫ / РАСПИСАНИЕ / БИЗНЕС (Phase 15);
// НИКА (чат) и КАБИНЕТ — прежние экраны, остаются у тренера.
// БИЗНЕС — ТОЛЬКО за флагами экономики (15-PLAN E1) — фильтруется в showShell.
const COACH_TABS: TabId[] = [
  "pulse",
  "athletes",
  "programs",
  "schedule",
  "business",
  "nika",
  "cabinet",
];
// v2.1 Phase 23 (UX-12): мобильная навигация тренера — 5 элементов, остальное
// в «Ещё». На desktop (≥760px) — sidebar со всеми вкладками.
const MOBILE_COACH_TABS: TabId[] = ["pulse", "athletes", "schedule", "nika"];

function showLogin(): void {
  closeShell();
  renderLogin(root, () => {
    void enterApp();
  });
}

/** После auth: PD-гейт → shell (гейт может вернуть 403 и позже из чата). */
async function enterApp(): Promise<void> {
  try {
    const gate = await fetchPdGate();
    if (gate) {
      renderPdGate(root, () => void showShell());
      return;
    }
  } catch {
    /* fail-open: серверный 403 из чата вернёт на гейт (onPdRequired) */
  }
  void showShell();
}

async function showShell(): Promise<void> {
  closeShell();
  let isCoach = false;
  try {
    isCoach = Boolean((await getMeContext()).is_coach);
  } catch {
    /* неизвестная роль → participant-набор (fail-soft, не fail-closed) */
  }
  // §7 «за флагами экономики» (DoD-9): БИЗНЕС-таб тренера показывается, только
  // когда включён хотя бы один из PAYMENTS_ENABLED/ECONOMY_ENABLED/PAYOUTS_ENABLED.
  // Снимок флагов недоступен → таб скрыт (данные не показываем).
  let businessOn = false;
  try {
    const fl = (await getFeatures()).flags;
    businessOn = Boolean(
      fl.ECONOMY_ENABLED?.enabled ||
        fl.PAYMENTS_ENABLED?.enabled ||
        fl.PAYOUTS_ENABLED?.enabled,
    );
  } catch {
    /* fail-soft: без /config/features БИЗНЕС скрыт */
  }
  let tabs: TabId[] = isCoach ? [...COACH_TABS] : [...PARTICIPANT_TABS];
  if (!businessOn) {
    tabs = tabs.filter((id) => id !== "business");
  }
  const narrow = window.matchMedia("(max-width: 760px)").matches;
  let moreTabs: TabId[] = [];
  if (isCoach && narrow) {
    moreTabs = tabs.filter((id) => !MOBILE_COACH_TABS.includes(id));
    tabs = [...MOBILE_COACH_TABS, "more"];
  }

  root.innerHTML = `
    <div class="shell">
      <header class="shell__header">
        <div class="shell__brand">
          <img class="shell__avatar" src="/brand/avatar.png" alt="" width="40" height="40">
          <div>
            <div class="shell__title">${t("app.title")}</div>
            <div class="shell__subtitle">${t("app.subtitle")}</div>
          </div>
        </div>
      </header>

      <main class="shell__screen" id="screen"></main>

      <nav class="shell__tabs" id="tabs">
        ${tabs
          .map(
            (id) =>
              `<button class="shell__tab" data-tab="${id}">${icon(id)}<span>${t(`tabs.${id}`)}</span></button>`,
          )
          .join("")}
      </nav>
    </div>
  `;

  const screen = root.querySelector<HTMLElement>("#screen")!;
  root.querySelector("#tabs")!.classList.toggle("shell__tabs--coach", isCoach);
  const chatHost = document.createElement("div");
  chatHost.className = "shell__pane";
  let chat: ChatView | null = null;
  disposeShell = () => chat?.dispose();
  const tabButtons = Array.from(
    root.querySelectorAll<HTMLButtonElement>(".shell__tab"),
  );

  // Бриф клиента открывается из ПУЛЬСА/АТЛЕТОВ/ПРОГРАММ — «назад» возвращает
  // на вкладку, из которой пришли.
  let briefReturn: TabId = "athletes";
  let activeTab: TabId | null = null;
  const openBrief =
    (from: TabId) =>
    (clientTg: number): void => {
      briefReturn = from;
      activeTab = null;
      const pane = document.createElement("div");
      pane.className = "shell__pane";
      screen.replaceChildren(pane);
      renderBriefScreen(pane, clientTg, () => renderTab(briefReturn));
    };

  const renderTab = (id: TabId): void => {
    if (id === activeTab) return;
    activeTab = id;
    const inMore = moreTabs.includes(id);
    for (const btn of tabButtons) {
      const btnTab = btn.dataset.tab as TabId;
      const isActive = btnTab === id || (inMore && btnTab === "more");
      btn.classList.toggle("shell__tab--active", isActive);
      if (isActive) btn.setAttribute("aria-current", "page");
      else btn.removeAttribute("aria-current");
    }
    chat?.deactivate();
    const pane = id === "nika" ? chatHost : document.createElement("div");
    pane.className = "shell__pane";
    screen.replaceChildren(pane);
    if (id === "more") {
      // v2.1 Phase 23 (UX-12): мобильное «Ещё» — меню скрытых вкладок.
      const menu = shell(
        "screen",
        `<h2 class="screen__title">${t("tabs.more")}</h2>` +
          moreTabs
            .map(
              (tid) =>
                `<button class="btn more__item" data-more="${tid}">${icon(tid)}<span>${t(`tabs.${tid}`)}</span></button>`,
            )
            .join(""),
      );
      pane.appendChild(menu);
      menu.querySelectorAll<HTMLButtonElement>("[data-more]").forEach((b) =>
        b.addEventListener("click", () => renderTab(b.dataset.more as TabId)),
      );
    } else if (id === "nika") {
      if (!chat) chat = renderChat(chatHost, {
        onPdRequired: () => {
          closeShell();
          renderPdGate(root, () => void showShell());
        },
        suggestChips: !isCoach, // UX-05: чипсы — только участнику
        suggestCoach: isCoach, // UX-09: «Итоги тренировки» — тренеру
      });
      chat.activate();
    } else if (id === "today") {
      renderTodayScreen(pane, () => renderTab("nika")); // UX-03: CTA → чат
    } else if (id === "progress") {
      renderProgressScreen(pane);
    } else if (id === "cabinet") {
      renderCabinetScreen(pane, () => { void logout().then(showLogin); });
    } else if (id === "athletes") {
      renderAthletesScreen(pane, openBrief("athletes"));
    } else if (id === "pulse") {
      renderPulseScreen(pane, openBrief("pulse"));
    } else if (id === "programs") {
      renderProgramsScreen(pane, openBrief("programs"), (text) => {
        renderTab("nika"); // UX-13: «Редактировать с Никой» → чат с контекстом
        chat?.prefill(text);
      });
    } else if (id === "schedule") {
      renderScheduleScreen(pane);
    } else if (id === "business") {
      renderBusinessScreen(pane);
    }
  };

  for (const btn of tabButtons) {
    btn.addEventListener("click", () => renderTab(btn.dataset.tab as TabId));
  }

  // Стартовая вкладка: тренер начинает с ПУЛЬСА (§7), участник — с чата.
  renderTab(isCoach ? "pulse" : "nika");
}

async function boot(): Promise<void> {
  const lang = initI18n();
  setLang(lang); // применяет <html lang> и сохранённый выбор
  const restored = hasSession() || (await restoreSession());
  if (restored) {
    await enterApp();
  } else {
    showLogin();
  }
}

void boot();
